import React, { useState, useEffect, useMemo } from 'react';

// Import our new utility functions and components
import { normalizeWineData } from '../../utils/wineDataUtils';
import WineCarousel from '../ui/WineCarousel';
import WineInfo from '../ui/WineInfo';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:5001';

const RecommendationsStep = ({
    recommendations = [],
    isLoadingRecommendations = false,
    errorFetchingRecommendations = null,
    restaurantSummary = '',
    isLoadingSummaries = false,
    errorFetchingSummaries = null,
    restaurant = '',
    isEatingIn = false,
    userPrompt = null,
    onBack,
    onStartOver,
    onViewMenu,
    isLoadingMenu = false,
    menuError = null
}) => {
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [selectedWineInfo, setSelectedWineInfo] = useState(null);
    const [isLoadingWineInfo, setIsLoadingWineInfo] = useState(false);
    const [submittedDecisions, setSubmittedDecisions] = useState({});
    const [showThankYou, setShowThankYou] = useState(false);
    const [thankYouTimeout, setThankYouTimeout] = useState(null);

    // Debug: Log userPrompt to see if it's being passed correctly
    useEffect(() => {
        console.log('RecommendationsStep received userPrompt:', userPrompt);
    }, [userPrompt]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (thankYouTimeout) {
                clearTimeout(thankYouTimeout);
            }
        };
    }, [thankYouTimeout]);


    // Normalize wine data for consistent UI
    const normalizedWines = useMemo(() => {
        if (!recommendations || recommendations.length === 0) return [];
        return recommendations.map(wine => normalizeWineData(wine, isEatingIn));
    }, [recommendations, isEatingIn]);

    const nextCard = () => {
        if (recommendations && currentCardIndex < recommendations.length - 1) {
            setCurrentCardIndex(currentCardIndex + 1);
        }
    };

    const previousCard = () => {
        if (currentCardIndex > 0) {
            setCurrentCardIndex(currentCardIndex - 1);
        }
    };

    const goToCard = (index) => {
        setCurrentCardIndex(index);
    };

    const handleWineSelection = async (wine) => {
        setIsLoadingWineInfo(true);
        setSelectedWineInfo(null);
        
        try {
            console.log('Wine data being sent to API:', wine.originalData);
            console.log('Is eating in:', isEatingIn);
            
            const wineInfoEndpoint = `${API_BASE_URL}/api/wine-info`;
            
            // Prepare request body matching the expected API format
            let requestBody;
            if (isEatingIn) {
                // For eating in - wine style recommendations
                requestBody = {
                    wine_style: wine.originalData.wine_style || '',
                    wine_type: wine.originalData.wine_type || '',
                    grape_varieties: wine.originalData.grape_varieties || '',
                    region: wine.originalData.region || '',
                    characteristics: wine.originalData.characteristics || '',
                    restaurant: '' // Empty for eating in
                };
            } else {
                // For eating out - specific restaurant wines
                requestBody = {
                    wine_name: wine.originalData.wine_name || '',
                    producer: wine.originalData.producer || '',
                    vintage: wine.originalData.vintage || '',
                    grapes: wine.originalData.grapes || '',
                    country: wine.originalData.country || '',
                    restaurant: restaurant || ''
                };
            }
            
            console.log('Request body being sent:', requestBody);
            
            const response = await fetch(wineInfoEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });
            
            if (response.ok) {
                const wineInfo = await response.json();
                console.log('Wine info response received:', wineInfo);
                console.log('Type of response:', typeof wineInfo);
                console.log('Response keys:', Object.keys(wineInfo));
                setSelectedWineInfo(wineInfo);
            } else {
                console.error('Failed to fetch wine info, status:', response.status);
                setSelectedWineInfo('Failed to load wine information. Please try again.');
            }
        } catch (error) {
            console.error('Error fetching wine info:', error);
            setSelectedWineInfo('Error loading wine information. Please try again.');
        } finally {
            setIsLoadingWineInfo(false);
        }
    };
    
    const handlePurchaseDecision = async (wineId, decision) => {
        // Check if already submitted for this wine
        if (submittedDecisions[wineId]) {
            return; // Prevent multiple submissions
        }
        
        // Mark as submitted
        setSubmittedDecisions(prev => ({
            ...prev,
            [wineId]: true
        }));
        
        
        // Show thank you message
        
        // Clear any existing timeout
        if (thankYouTimeout) {
            clearTimeout(thankYouTimeout);
        }
        
        setShowThankYou(true);
        
        // Set a new timeout
        const newTimeout = setTimeout(() => {
            setShowThankYou(false);
        }, 3000); // Hide after 3 seconds
        
        setThankYouTimeout(newTimeout);
        
        // Prepare data to log
        const currentWine = recommendations[currentCardIndex];
        
        console.log('About to log purchase decision. userPrompt is:', userPrompt);
        
        let logData;
        if (isEatingIn) {
            // Home cooking data structure
            logData = {
                timestamp: new Date().toISOString(),
                wine_id: wineId,
                wine_name: currentWine.wine_style,
                wine_type: currentWine.wine_type || '',
                grape_varieties: currentWine.grape_varieties || '',
                region: currentWine.region || '',
                characteristics: currentWine.characteristics || '',
                purchase_decision: decision,
                user_preferences: {
                    dining_location: 'eating_in',
                    wine_rank: currentWine.rank
                },
                // Include user prompt data
                user_prompt: userPrompt ? {
                    wine_preferences: userPrompt.wine_preferences || '',
                    food_preferences: userPrompt.food_preferences || '',
                    price_range: userPrompt.price_range || '',
                    prompt_timestamp: userPrompt.timestamp || ''
                } : null
            };
        } else {
            // Restaurant data structure
            logData = {
                timestamp: new Date().toISOString(),
                wine_id: wineId,
                wine_name: currentWine.wine_name || currentWine.producer || '',
                producer: currentWine.producer || '',
                restaurant: restaurant || '',
                purchase_decision: decision,
                bottle_price: currentWine.bottle_price || '',
                glass_price: currentWine.glass_price || '',
                grapes: currentWine.grapes || '',
                region: currentWine.region || '',
                characteristics: currentWine.characteristics || '',
                user_preferences: {
                    dining_location: 'eating_out',
                    wine_rank: currentWine.rank
                },
                // Include user prompt data
                user_prompt: userPrompt ? {
                    wine_preferences: userPrompt.wine_preferences || '',
                    food_preferences: userPrompt.food_preferences || '',
                    price_range: userPrompt.price_range || '',
                    restaurant: userPrompt.restaurant || '',
                    prompt_timestamp: userPrompt.timestamp || ''
                } : null
            };
        }
        
        try {
            // Determine which endpoint to use based on dining location
            const endpoint = isEatingIn ? `${API_BASE_URL}/api/log-purchase-decision-home` : `${API_BASE_URL}/api/log-purchase-decision-restaurant`;
            
            // Send to backend to log in appropriate CSV
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(logData)
            });
            
            if (!response.ok) {
                console.error('Failed to log purchase decision');
            }
        } catch (error) {
            console.error('Error logging purchase decision:', error);
        }
    };
    if (isLoadingRecommendations) {
        return (
            <div className="step-container">
                <div className="loading-message">
                    {isEatingIn ? 'Generating wine recommendations...' : 'I\'m carefully selecting the perfect wines for you...'}
                </div>
                <div className="navigation-buttons">
                    <button onClick={onBack}>Back</button>
                </div>
            </div>
        );
    }

    if (errorFetchingRecommendations) {
        return (
            <div className="step-container">
                <div className="error-message">
                    Failed to load recommendations. Please try again.
                </div>
                <div className="navigation-buttons">
                    <button onClick={onBack}>Back</button>
                    <button onClick={onStartOver}>Start Over</button>
                </div>
            </div>
        );
    }

    // Unified UI using our normalized data and reusable components
    return (
        <div className="step-container">
            {/* Restaurant Overview Section (only for eating out) */}
            {!isEatingIn && restaurant && (
                <div className="restaurant-overview-section">
                    <h2>Restaurant Overview</h2>
                    {isLoadingSummaries ? (
                        <p className="message-text">Loading restaurant information...</p>
                    ) : errorFetchingSummaries ? (
                        <p className="message-text error-text">Failed to load restaurant information.</p>
                    ) : restaurantSummary && (
                        <div className="restaurant-summary">
                            <h3>About {restaurant}</h3>
                            <p>{restaurantSummary}</p>
                        </div>
                    )}
                </div>
            )}
            
            {/* Recommendations Section */}
            <div className="recommended-wines-section">
                <h2>
                    {isEatingIn 
                        ? "Wine Recommendations for Your Home Cooking" 
                        : (restaurant ? "Recommended Wines" : "Here are my wine recommendations for you:")
                    }
                </h2>
                
                {isLoadingRecommendations ? (
                    <p className="message-text">
                        {isEatingIn ? 'Generating wine recommendations...' : 'I\'m carefully selecting the perfect wines for you...'}
                    </p>
                ) : errorFetchingRecommendations ? (
                    <p className="message-text error-text">Failed to load recommendations. Please try again.</p>
                ) : normalizedWines && normalizedWines.length > 0 ? (
                    <div>
                        <div className="wine-styles-intro">
                            <p className="message-text">
                                {isEatingIn 
                                    ? "Here are wine styles that would pair beautifully with your cooking:" 
                                    : "Here are wines from the menu that would pair perfectly with your preferences:"
                                }
                            </p>
                        </div>
                        
                        {/* Wine Card Carousel using our reusable component */}
                        <WineCarousel
                            wines={normalizedWines}
                            currentIndex={currentCardIndex}
                            onPrevious={previousCard}
                            onNext={nextCard}
                            onSelect={goToCard}
                            onTellMeMore={() => handleWineSelection(normalizedWines[currentCardIndex])}
                            isLoading={isLoadingWineInfo}
                        />
                        
                        {/* Wine Details or Instructions - appears in place of each other */}
                        {isLoadingWineInfo ? (
                            <div className="wine-info-message loading">
                                Loading additional wine information...
                            </div>
                        ) : selectedWineInfo ? (
                            <WineInfo
                                wineInfo={selectedWineInfo}
                                currentWine={normalizedWines[currentCardIndex]}
                                onPurchaseDecision={handlePurchaseDecision}
                                purchaseDecisions={{}}
                                submittedDecisions={submittedDecisions}
                                isEatingIn={isEatingIn}
                            />
                        ) : (
                            <div className="wine-info-message">
                                <p>Click "Tell me more" on any wine card to get detailed information.</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="message-text">No recommendations available. Please try adjusting your preferences.</p>
                )}
            </div>

            {/* Thank You Modal */}
            {showThankYou && (
                <div className="thank-you-overlay">
                    <div className="thank-you-message">
                        <h3>Thank you for your purchase decision!</h3>
                        <p>Your choice has been logged and will help improve future recommendations.</p>
                    </div>
                </div>
            )}
            
            <div className="navigation-buttons">
                {!isEatingIn && restaurant && (
                    <button 
                        onClick={onViewMenu}
                        disabled={isLoadingMenu}
                    >
                        {isLoadingMenu ? 'Preparing Menu...' : 'View Menu'}
                    </button>
                )}
                <button onClick={onStartOver}>Start Over</button>
            </div>

            {!isEatingIn && menuError && (
                <p className="message-text error-text" role="alert">{menuError}</p>
            )}
        </div>
    );
};

export default RecommendationsStep;
